import * as React from 'react';
import {Mutation} from 'react-apollo';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Button} from 'shells/button/button';
import {ButtonMover} from 'shells/button-mover/button-mover';
import {colors} from 'shells/colors/colors';
import {FormField} from 'shells/form/form-field';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {RadioButton} from 'shells/radio/radio-button';
import {RadioGroup} from 'shells/radio/radio-group';
import {SpacerGroup} from 'shells/spacer/spacer-group';
import {TextfieldWithApply} from 'shells/textfield/textfield-with-apply';
import {ModalBody} from 'shells/typography/modal-body';

import type {
    UserEmail as UserEmailFragment,
    createUserEmailAddress as CreateUserEmailAddressMutation,
    createUserEmailAddressVariables as CreateUserEmailAddressVariables,
} from 'nutshell-graphql-types';

import {GetEmailSendingSettings} from '../../email-sending/get-email-sending-settings';
import CREATE_USER_EMAIL_ADDRESS from '../../graphql/mutations/create-user-email-address.graphql';

import styles from './user-email-address-selection.module.css';

type Props = {
    selectedAddress: string | null | undefined;
    onSelectAddress: (arg1?: UserEmailFragment | null | undefined) => void;
};

class CreateUserEmailAddress extends Mutation<
    CreateUserEmailAddressMutation,
    CreateUserEmailAddressVariables
> {}

export function UserEmailAddressSelection(props: Props) {
    const [isAddingNewEmail, setIsAddingNewEmail] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);

    return (
        <CreateUserEmailAddress mutation={CREATE_USER_EMAIL_ADDRESS}>
            {(userEmailCreate, {loading: mutationInFlight}) => (
                <GetEmailSendingSettings>
                    {({isLoading, emailAddresses}) => {
                        if (isLoading) {
                            return <LoadingIcon fill={colors.offWhiteDk} />;
                        }

                        const configurableAddresses = emailAddresses.filter((address) => {
                            return address.type === 'UNCONFIGURED';
                        });

                        if (!props.selectedAddress && configurableAddresses.length) {
                            props.onSelectAddress(configurableAddresses[0]);
                        }

                        function handleAddressSelection(selectedId: string) {
                            props.onSelectAddress(
                                configurableAddresses.find((address) => {
                                    return address.id === selectedId;
                                })
                            );
                        }

                        return (
                            <div>
                                <SpacerGroup mb={16}>
                                    {configurableAddresses.length ? (
                                        <FormField
                                            innerId='emailAddresses'
                                            label='Your email addresses'
                                        >
                                            <div className={styles.addresses}>
                                                <RadioGroup
                                                    name='emailAddresses'
                                                    selectedValue={props.selectedAddress}
                                                    onChange={handleAddressSelection}
                                                >
                                                    {configurableAddresses.map((address) => {
                                                        return (
                                                            <RadioButton
                                                                key={address.id}
                                                                value={address.id}
                                                            >
                                                                {address.address}
                                                            </RadioButton>
                                                        );
                                                    })}
                                                </RadioGroup>
                                            </div>
                                        </FormField>
                                    ) : (
                                        <ModalBody>
                                            <i>
                                                It looks like you’ve configured email for all of
                                                your Nutshell email addresses. Disable an existing
                                                connection or add another below to continue.
                                            </i>
                                        </ModalBody>
                                    )}
                                </SpacerGroup>
                                {isAddingNewEmail ? (
                                    <React.Fragment>
                                        <SpacerGroup mb={8}>
                                            <TextfieldWithApply
                                                type='email'
                                                disabled={mutationInFlight}
                                                buttonText='Add'
                                                errorMessage={errorMessage || undefined}
                                                onEscape={() => {
                                                    setIsAddingNewEmail(false);
                                                }}
                                                // @ts-expect-error
                                                onApply={(val) => {
                                                    setErrorMessage(null);
                                                    userEmailCreate({
                                                        variables: {
                                                            emailAddress: val,
                                                        },
                                                        refetchQueries: ['GetSyncStatuses'],
                                                    })
                                                        .then((result) => {
                                                            if (result && result.data) {
                                                                props.onSelectAddress(
                                                                    result.data.userEmailCreate
                                                                        .userEmail
                                                                );
                                                            }
                                                            setIsAddingNewEmail(false);
                                                        })
                                                        .catch((err) => {
                                                            setErrorMessage(
                                                                safelyGetGraphQLErrorMessage(err)
                                                            );
                                                        });
                                                }}
                                                autoFocus={true}
                                                placeholder='me@example.com'
                                            />
                                        </SpacerGroup>
                                    </React.Fragment>
                                ) : (
                                    <ButtonMover direction='left' size='big'>
                                        <Button
                                            onClick={() => setIsAddingNewEmail(true)}
                                            size='big'
                                            variant='text-primary'
                                        >
                                            + Use a different email address
                                        </Button>
                                    </ButtonMover>
                                )}
                            </div>
                        );
                    }}
                </GetEmailSendingSettings>
            )}
        </CreateUserEmailAddress>
    );
}
